import React, { useMemo, useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { HiOutlineBolt, HiOutlineCheckCircle, HiOutlineUserGroup } from "react-icons/hi2";
import { baseURL } from "../config";

const api = axios.create({ baseURL });

const ServiceRequestForm = ({ mode = "restaurant", className = "" }) => {
  const [businessName, setBusinessName] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [comment, setComment] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fields = useMemo(() => {
    if (mode === "restaurant") {
      return {
        title: "Restaurant Request",
        subtitle: "Share your restaurant details and our team will follow up.",
        type: "Restaurant",
        primaryLabel: "Business Name",
        primaryValue: businessName,
        setPrimaryValue: setBusinessName,
        primaryPlaceholder: "Enter business name",
        needsAddress: true,
        highlights: [
          {
            icon: HiOutlineBolt,
            text: "Fast onboarding for restaurant partners",
          },
          {
            icon: HiOutlineUserGroup,
            text: "Dedicated follow-up from our team",
          },
          {
            icon: HiOutlineCheckCircle,
            text: "Simple process with clear next steps",
          },
        ],
      };
    }

    if (mode === "taxi") {
      return {
        title: "Taxi Request",
        subtitle: "Tell us who is booking so we can respond quickly.",
        type: "Taxi",
        primaryLabel: "Customer Name",
        primaryValue: customerName,
        setPrimaryValue: setCustomerName,
        primaryPlaceholder: "Enter customer name",
        needsAddress: true,
        highlights: [
          {
            icon: HiOutlineBolt,
            text: "Quick review for taxi-related requests",
          },
          {
            icon: HiOutlineUserGroup,
            text: "Clear communication after submission",
          },
          {
            icon: HiOutlineCheckCircle,
            text: "Easy booking and service follow-up",
          },
        ],
      };
    }

    return {
      title: "Delivery Partner Request",
      subtitle: "Leave your details and we will get back to you.",
      type: "Deliveryman",
      primaryLabel: "Name",
      primaryValue: name,
      setPrimaryValue: setName,
      primaryPlaceholder: "Enter your name",
      needsAddress: false,
      highlights: [
        {
          icon: HiOutlineBolt,
          text: "Flexible work opportunities",
        },
        {
          icon: HiOutlineUserGroup,
          text: "Support during onboarding",
        },
        {
          icon: HiOutlineCheckCircle,
          text: "Simple application flow",
        },
      ],
    };
  }, [businessName, customerName, mode, name]);

  const resetForm = () => {
    setBusinessName("");
    setCustomerName("");
    setName("");
    setPhone("");
    setAddress("");
    setComment("");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (isSubmitting) return;

    const payload = {
      type: fields.type,
      phone,
      comment,
    };

    if (mode === "restaurant") {
      payload.businessName = businessName;
      payload.address = address;
    } else if (mode === "taxi") {
      payload.customerName = customerName;
      payload.address = address;
    } else {
      payload.name = name;
    }

    const missingBusiness = mode === "restaurant" && !payload.businessName;
    const missingCustomer = mode === "taxi" && !payload.customerName;
    const missingDeliveryName = mode === "deliveryman" && !payload.name;
    const missingAddress = fields.needsAddress && !payload.address;

    if (
      !payload.type ||
      !payload.phone ||
      !payload.comment ||
      missingBusiness ||
      missingCustomer ||
      missingDeliveryName ||
      missingAddress
    ) {
      toast.error("Please fill out all required fields.");
      return;
    }

    let loadingToastId;
    try {
      setIsSubmitting(true);
      loadingToastId = toast.loading("Submitting your request...");
      await api.post("/service-request/create", payload);
      toast.dismiss(loadingToastId);
      toast.success("Your request was submitted successfully.");
      resetForm();
    } catch (error) {
      if (loadingToastId) toast.dismiss(loadingToastId);
      const message =
        error?.response?.data?.message || "Failed to submit request.";
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className={`px-4 py-16 sm:px-6 lg:px-8 ${className}`}>
      <div className="mx-auto grid max-w-6xl gap-8 rounded-[1.75rem] bg-gradient-to-br from-slate-950 via-slate-900 to-emerald-950 p-6 text-white shadow-2xl lg:grid-cols-[1.05fr_0.95fr] lg:p-10">
        <div className="space-y-6">
          <span className="inline-flex rounded-full border border-white/15 bg-white/10 px-4 py-1 text-sm font-medium text-emerald-200">
            Contact form
          </span>
          <h2 className="max-w-xl text-3xl font-semibold leading-tight sm:text-4xl">
            {fields.title}
          </h2>
          <p className="max-w-lg text-sm leading-6 text-slate-300 sm:text-base">
            {fields.subtitle}
          </p>
          <div className="grid gap-3 rounded-2xl border border-white/10 bg-white/5 p-4 text-sm text-slate-200 sm:grid-cols-2">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-emerald-300">
                Response time
              </p>
              <p className="mt-1 font-medium">Within one business day</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-emerald-300">
                Submission type
              </p>
              <p className="mt-1 font-medium">{fields.type}</p>
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-3">
            {fields.highlights.map((item) => {
              const Icon = item.icon;

              return (
              <div
                key={item.text}
                className="rounded-2xl border border-white/10 bg-white/[0.06] px-4 py-4"
              >
                <div className="mb-3 inline-flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-400/12 text-emerald-300">
                  <Icon className="text-xl" />
                </div>
                <p className="text-sm leading-6 text-slate-200">{item.text}</p>
              </div>
              );
            })}
          </div>
        </div>

        <form
          onSubmit={handleSubmit}
          className="rounded-[1.4rem] bg-white p-5 text-slate-900 shadow-xl sm:p-6"
        >
          <div className="mb-5">
            <h3 className="text-xl font-semibold text-slate-900">
              Send request
            </h3>
            <p className="mt-1 text-sm text-slate-500">
              All fields are saved in the admin panel.
            </p>
          </div>

          <div className="grid gap-4">
            <label className="block text-sm font-medium text-slate-700">
              {fields.primaryLabel}
              <input
                type="text"
                value={fields.primaryValue}
                onChange={(event) => fields.setPrimaryValue(event.target.value)}
                placeholder={fields.primaryPlaceholder}
                disabled={isSubmitting}
                className="mt-1 w-full rounded-xl border border-slate-300 bg-slate-50 px-4 py-3 outline-none transition placeholder:text-slate-400 focus:border-emerald-500 focus:bg-white"
              />
            </label>

            <label className="block text-sm font-medium text-slate-700">
              Phone
              <input
                type="tel"
                value={phone}
                onChange={(event) =>
                  setPhone(event.target.value.replace(/[^0-9+]/g, ""))
                }
                placeholder="Enter phone number"
                disabled={isSubmitting}
                className="mt-1 w-full rounded-xl border border-slate-300 bg-slate-50 px-4 py-3 outline-none transition placeholder:text-slate-400 focus:border-emerald-500 focus:bg-white"
              />
            </label>

            {fields.needsAddress && (
              <label className="block text-sm font-medium text-slate-700">
                Address
                <input
                  type="text"
                  value={address}
                  onChange={(event) => setAddress(event.target.value)}
                  placeholder="Enter address"
                  disabled={isSubmitting}
                  className="mt-1 w-full rounded-xl border border-slate-300 bg-slate-50 px-4 py-3 outline-none transition placeholder:text-slate-400 focus:border-emerald-500 focus:bg-white"
                />
              </label>
            )}

            <label className="block text-sm font-medium text-slate-700">
              Comment
              <textarea
                value={comment}
                onChange={(event) => setComment(event.target.value)}
                placeholder="Write your comment"
                rows={3}
                disabled={isSubmitting}
                className="mt-1 w-full rounded-xl border border-slate-300 bg-slate-50 px-4 py-3 outline-none transition placeholder:text-slate-400 focus:border-emerald-500 focus:bg-white"
              />
            </label>
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="mt-6 inline-flex w-full items-center justify-center rounded-xl bg-emerald-600 px-4 py-3 font-semibold text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isSubmitting ? "Submitting..." : "Submit request"}
          </button>
        </form>
      </div>
    </section>
  );
};

export default ServiceRequestForm;
